import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BackMethods from "../../back_component/BackMethods";

export default function InspectionMissionsPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { backEnd_request, getUserConstraints } = BackMethods();
    // جلب معلومات المستخدم وصلاحياته
    const { userId, isSuperAdmin } = getUserConstraints();
    // --- الحالات (States) ---
    const [missions, setMissions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    // الفلاتر
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [statusFilter, setStatusFilter] = useState("");
    // جلب المعاملات من الرابط (URL Params) إن وجدت
    const urlAssignedToId = searchParams.get("userId");
    const urlEstablishmentId = searchParams.get("establishmentId");
    // دالة لتحويل كود الحالة إلى نص ولون
    const getStatusDetails = (status) => {
        switch (status) {
            case 0: return { label: "قيد الانتظار", color: "bg-warning text-dark", icon: "bi-clock-history" };
            case 2: return { label: "تم الإنجاز", color: "bg-success text-white", icon: "bi-check-circle" };
            case 3: return { label: "ملغاة", color: "bg-danger text-white", icon: "bi-x-circle" };
            default: return { label: "غير معروف", color: "bg-secondary text-white", icon: "bi-question-circle" };
        }
    };
    const fetchMissions = async () => {
        setIsLoading(true);
        try {
            // إعداد البارامترات بناءً على القواعد المطلوبة
            const params = {
                page: currentPage,
                size: pageSize,
                status: statusFilter !== "" ? statusFilter : undefined,
            };

            /** * منطق الفلترة المتقدم:
             * 1- إذا مررنا establishmentId في الرابط
             * 2- إذا مررنا userId (المسند إليه) في الرابط
             * 3- إذا لم يكن المستخدم Admin، نجلب المهام الخاصة به فقط تلقائياً
             */
            if (urlEstablishmentId) {
                params.establishmentId = urlEstablishmentId;
            } else if (urlAssignedToId) {
                params.assignedToUserId = urlAssignedToId;
            } else if (!isSuperAdmin) {
                // إذا لم يكن أدمن ولم يتم تحديد فلتر خاص، يرى مهامه فقط
                params.assignedToUserId = userId;
            }

            const res = await backEnd_request.get("/InspectionMissions", { params });

            if (res.data) {
                setMissions(res.data.data || []);
                setTotalCount(res.data.total || 0);
                setTotalPages(Math.ceil((res.data.total || 0) / pageSize));
            }
        } catch (error) {
            console.error("خطأ في جلب المهام:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchMissions();
    }, [currentPage, statusFilter, urlAssignedToId, urlEstablishmentId]);

    return (
        <div className="container py-4" style={{ direction: "rtl", textAlign: "right" }}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="fw-bold">قائمة المهام المسندة</h3>
                    <p className="text-muted small">إجمالي المهام المكتشفة: {totalCount}</p>
                </div>

                <div className="d-flex gap-2">
                    <select
                        className="form-select form-select-sm shadow-sm"
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                        style={{ width: "150px" }}
                    >
                        <option value="">كل الحالات</option>
                        <option value="0">انتظار</option>
                        <option value="2">تم الإنجاز</option>
                        <option value="3">ملغاة</option>
                    </select>
                </div>
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                </div>
            ) : missions.length === 0 ? (
                <div className="card shadow-sm border-0 text-center py-5">
                    <i className="bi bi-inbox text-muted display-1"></i>
                    <h5 className="mt-3">لا توجد مهام حالياً</h5>
                </div>
            ) : (
                <div className="row g-3">
                    {missions.map((mission) => {
                        const statusInfo = getStatusDetails(mission.status);
                        return (
                            <div key={mission.missionId} className="col-12">
                                <div className="card shadow-sm border-0 border-start border-4 transition-all"
                                    style={{ borderStartColor: statusInfo.color.includes('success') ? '#198754' : statusInfo.color.includes('warning') ? '#ffc107' : '#dc3545' }}>
                                    <div className="card-body">
                                        <div className="row align-items-center">
                                            <div className="col-md-4">
                                                <h6 className="fw-bold mb-1">{mission.establishmentName}</h6>
                                                <div className="badge bg-light text-dark border small">
                                                    <i className="bi bi-tag-fill me-1"></i> {mission.missionType}
                                                </div>
                                            </div>

                                            <div className="col-md-3">
                                                <div className="small text-muted">الدورية:</div>
                                                <div className="small fw-bold">
                                                    <i className="bi bi-person-fill"></i> {mission.assignedToUsername ?? "مفتش ميداني"}
                                                </div>
                                            </div>

                                            <div className="col-md-2 text-center">
                                                <span className={`badge rounded-pill px-3 py-2 ${statusInfo.color}`}>
                                                    <i className={`bi ${statusInfo.icon} me-1`}></i> {statusInfo.label}
                                                </span>
                                            </div>

                                            <div className="col-md-3 text-start">
                                                <button
                                                    className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                                    onClick={() => mission.linkedVisitId && navigate(`/admin/visit-details/${mission.linkedVisitId}`)}
                                                    disabled={!mission.linkedVisitId}
                                                >
                                                    {mission.linkedVisitId ? "عرض الزيارة" : "لا يوجد رابط"}
                                                </button>
                                            </div>
                                        </div>

                                        <hr className="my-2 opacity-25" />

                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="small text-secondary">
                                                <i className="bi bi-info-circle me-1"></i> {mission.instructions}
                                            </div>
                                            <div className="extra-small text-muted" style={{ fontSize: '0.75rem' }}>
                                                أسندت بتاريخ: {new Date(mission.assignedAtUtc).toLocaleDateString('ar-SY')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <nav className="mt-4">
                    <ul className="pagination justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link shadow-none" onClick={() => setCurrentPage(p => p - 1)}>السابق</button>
                        </li>
                        {[...Array(totalPages)].map((_, i) => (
                            <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                <button className="page-link shadow-none" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                            </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link shadow-none" onClick={() => setCurrentPage(p => p + 1)}>التالي</button>
                        </li>
                    </ul>
                </nav>
            )}
        </div>
    );
}